import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, map, of, startWith, switchMap, tap } from 'rxjs';
import { PaywallService } from '../../services/paywall.service';
import { CheckoutService } from '../../services/checkout.service';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { PricePipe } from "../../pipes/price.pipe";
import { CommonModule } from '@angular/common';
import { isQuantityChargePlan, quantityCharges, seatCountLabel } from '../../helpers/quantity.helper';
import { Charge, VatValidationStatus } from '../../models/paywall-settings';
import { validationMessages } from '../../helpers/http-error.helper';

export type VatFieldStatus = 'idle' | 'checking' | VatValidationStatus;

const VAT_MIN_LENGTH = 4;
const VAT_DEBOUNCE_MS = 600;
const INTEGER_FIELD_TYPE = 1;
const CHECKBOX_FIELD_TYPE = 2;

// Same limits as the API (PaywallCheckoutFields). The company name becomes the Stripe customer name, which
// Stripe caps at 256 characters, and Stripe rejects an email without a top-level domain such as name@company.
export const FIELD_MAX_LENGTHS = {
  company_legal_name: 256,
  email: 320,
  street: 200,
  street_2: 200,
  city: 100,
  state: 100,
  zip: 20,
  org_number: 50,
  vat_number: 50
} as const;
const DELIVERABLE_EMAIL = /^[^@\s]+@(?:[^@\s.]+\.)+[^@\s.]{2,}$/;

const PAYWALL_FAILURE_MESSAGE = "We couldn't start the payment. Please check your details and try again.";
const PURCHASE_REJECTED_MESSAGE = "We couldn't complete your purchase. Please check your details and try again.";

// Validators.required accepts a value of only spaces, which the API rejects as empty.
const notBlank: ValidatorFn = control =>
  typeof control.value === 'string' && control.value.length > 0 && !control.value.trim() ? { required: true } : null;
const requiredText = [Validators.required, notBlank];

const trimmed = (value: unknown) => typeof value === 'string' ? value.trim() : value;

// Checks the address without surrounding spaces, as it is sent. A pasted address often ends in an invisible space.
const deliverableEmail: ValidatorFn = control => {
  const value = new FormControl(trimmed(control.value));
  return Validators.email(value) ?? Validators.pattern(DELIVERABLE_EMAIL)(value);
};

// Younium stores an integer custom field as a 64-bit number, so the API (PaywallCheckoutFields) accepts the same range.
const WHOLE_NUMBER = /^-?\d+$/;
const INT64_MIN = -(2n ** 63n);
const INT64_MAX = 2n ** 63n - 1n;

// Checked without surrounding spaces, as it is sent. Empty is left to the required validators.
const wholeNumber: ValidatorFn = control => {
  const value = trimmed(control.value);
  if (typeof value !== 'string' || !value) {
    return null;
  }
  if (!WHOLE_NUMBER.test(value)) {
    return { wholeNumber: true };
  }
  const number = BigInt(value);
  return number < INT64_MIN || number > INT64_MAX ? { wholeNumber: true } : null;
};

const VAT_STATUS_MESSAGES: Record<VatValidationStatus, string> = {
  Valid: 'VAT number verified in the EU VIES register.',
  Invalid: 'This VAT number could not be found in the EU VIES register. You can still complete the purchase, but VAT will be charged.',
  Unverifiable: "We couldn't verify this VAT number right now (VIES unavailable or country not covered). VAT may be charged.",
  CountryMismatch: 'This VAT number belongs to a different country than the one selected.'
};

@Component({
  selector: 'yon-pw-paywall-checkout',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PricePipe
  ],
  providers: [PricePipe],
  templateUrl: './paywall-checkout.component.html',
  styleUrl: './paywall-checkout.component.scss',
  standalone: true,
})
export class PaywallCheckoutComponent {
  public paywallService: PaywallService = inject(PaywallService);
  checkoutService: CheckoutService = inject(CheckoutService);
  formBuilder: FormBuilder = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  //readonly dialog = inject(MatDialog);
  public accountFields: any[] = this.paywallService.accountFields();
  public orderFields: any[] = this.paywallService.orderFields();
  public customFields: any[] = [...this.accountFields, ...this.orderFields];

  public readonly addressFields = [
    { name: 'street', label: 'Street' },
    { name: 'street_2', label: 'Street 2' },
    { name: 'zip', label: 'Zip' },
    { name: 'city', label: 'City' },
    { name: 'state', label: 'State' },
    { name: 'country', label: 'Country' }
  ];

  public purchaseInProgress = computed(() => this.checkoutService.loadingState().loading);

  public countries = computed(() => this.paywallService.countries());

  public viesValidationEnabled = computed(() => {
    const fields = this.paywallService.settings()?.checkoutFields;
    return !!(fields?.vatNumberEnabled && fields?.viesValidationEnabled);
  });
  public vatStatus = signal<VatFieldStatus>('idle');
  public vatStatusMessage = computed(() => {
    const status = this.vatStatus();
    // A mismatch has no status icon; it is shown as the field's error instead.
    return status === 'idle' || status === 'checking' || status === 'CountryMismatch' ? '' : VAT_STATUS_MESSAGES[status];
  });

  private readonly pricePipe = inject(PricePipe);

  public selectedPlan = computed(() => this.paywallService.selectedChargePlan());

  public isQuantityPurchase = computed(() => isQuantityChargePlan(this.selectedPlan()));

  public summaryTitle = computed(() => this.selectedPlan()?.name);

  public billingPeriodLabel = computed(() => {
    switch (this.paywallService.selectedBillingPeriod().value) {
      case 1: return 'Per quarter';
      case 2: return 'Per year';
      default: return 'Per month';
    }
  });

  public seats = computed(() => this.paywallService.quantityFor(this.selectedPlan()));

  public seatsLabel = computed(() => seatCountLabel(this.seats()));

  public quantityChargeLines = computed(() => quantityCharges(this.selectedPlan()));

  public hasOneTime = computed(() => this.pricePipe.hasOneTime(this.selectedPlan()));

  public oneTimeLabel = computed(() => this.pricePipe.oneTimeLabel(this.selectedPlan(), this.seats()));

  chargeLabel(charge: Charge): string {
    return this.pricePipe.chargeLabel(charge, this.seats());
  }

  unitLabel(charge: Charge): string {
    return this.pricePipe.unitLabel(charge);
  }

  checkoutForm!: FormGroup;

  constructor() {
    // Logged by the service; Buy now connects again.
    this.checkoutService.startConnection().subscribe({ error: () => undefined });

    // The failure carries only the session id and severity, never a message to show the buyer.
    this.checkoutService.receiveMessage('PaywallFailure').pipe(takeUntilDestroyed()).subscribe(() => {
      this.checkoutService.loadingState.set({
        loading: false,
        error: true,
        message: PAYWALL_FAILURE_MESSAGE
      });
    });

    this.checkoutService.receiveMessage('paymentinitialized').pipe(takeUntilDestroyed()).subscribe((message: any) => {
      console.log('paymentinitialized:', message);
      this.checkoutService.loadingState.set({
        loading: false,
        error: false,
        message: message.metadata.SessionId
      });

      // Test mode: no Stripe checkout, the buyer simulates the payment instead.
      if (message.metadata.Simulated === 'true') {
        this.paywallService.simulatedPayment.set({
          amount: Number(message.metadata.Amount),
          subtotal: Number(message.metadata.Subtotal),
          tax: Number(message.metadata.Tax),
          currencyCode: message.metadata.CurrencyCode
        });
        this.paywallService.step.set('simulate');
        return;
      }

      if (message.metadata.SessionUrl) {
        location.href = message.metadata.SessionUrl;
      }
    });

    var accountFields: any = {};
    this.accountFields.forEach((field: any) => {
      accountFields[field.id] = this.customFieldControl(field);
    });
    var orderFields: any = {};
    this.orderFields.forEach((field: any) => {
      orderFields[field.id] = this.customFieldControl(field);
    });

    var checkoutFields: any = {};
    if (this.paywallService.settings()?.checkoutFields?.organizationNumberEnabled) {
      checkoutFields.org_number = ['', [...requiredText, Validators.maxLength(FIELD_MAX_LENGTHS.org_number)]];
    }
    // Optional: buyers outside the EU have no VAT number. Leaving it empty means VAT is charged.
    if (this.paywallService.settings()?.checkoutFields?.vatNumberEnabled) {
      checkoutFields.vat_number = ['', Validators.maxLength(FIELD_MAX_LENGTHS.vat_number)];
    }


    this.checkoutForm = this.formBuilder.group({
      street: ['', [...requiredText, Validators.maxLength(FIELD_MAX_LENGTHS.street)]],
      street_2: ['', Validators.maxLength(FIELD_MAX_LENGTHS.street_2)],
      zip: ['', [...requiredText, Validators.maxLength(FIELD_MAX_LENGTHS.zip)]],
      city: ['', [...requiredText, Validators.maxLength(FIELD_MAX_LENGTHS.city)]],
      state: ['', [...requiredText, Validators.maxLength(FIELD_MAX_LENGTHS.state)]],
      country: ['', Validators.required],
      company_legal_name: ['', [...requiredText, Validators.maxLength(FIELD_MAX_LENGTHS.company_legal_name)]],
      ...checkoutFields,
      email: ['', [...requiredText, deliverableEmail, Validators.maxLength(FIELD_MAX_LENGTHS.email)]],
      // payment_method: ['', Validators.required],
      currencyCode: [this.paywallService.selectedCurrency()?.code || '', Validators.required],
      terms_and_conditions: [false, Validators.requiredTrue],
      billingPeriod: [this.paywallService.selectedBillingPeriod().value, Validators.required],
      ...accountFields,
      ...orderFields
    });

    this.restoreDraft();

    if (this.viesValidationEnabled()) {
      this.watchVatNumber();
    }
  }

  // Back unmounts the checkout, so what the buyer typed is kept in the service for when they select a card again.
  // Currency and billing period follow the new selection, and the terms are accepted again for the new order.
  private restoreDraft(): void {
    const draft = this.paywallService.checkoutDraft;
    if (draft) {
      const { currencyCode, billingPeriod, terms_and_conditions, ...typed } = draft;
      this.checkoutForm.patchValue(typed);
    }
    this.destroyRef.onDestroy(() => this.paywallService.checkoutDraft = this.checkoutForm.getRawValue());
  }

  private watchVatNumber(): void {
    const vatControl = this.checkoutForm.get('vat_number')!;
    const countryControl = this.checkoutForm.get('country')!;

    // A mismatch blocks Buy now, like the backend does. Re-validated on every status change, so a new
    // VAT number or country clears it right away.
    vatControl.addValidators(() => this.vatStatus() === 'CountryMismatch' ? { countryMismatch: true } : null);
    const setVatStatus = (status: VatFieldStatus) => {
      this.vatStatus.set(status);
      vatControl.updateValueAndValidity({ emitEvent: false });
      if (status === 'CountryMismatch') {
        vatControl.markAsTouched();
      }
    };

    combineLatest([
      vatControl.valueChanges.pipe(startWith(vatControl.value)),
      countryControl.valueChanges.pipe(startWith(countryControl.value))
    ]).pipe(
      map(([vat, countryId]) => ({ vat: (vat ?? '').trim(), countryId: countryId ?? '' })),
      distinctUntilChanged((a, b) => a.vat === b.vat && a.countryId === b.countryId),
      // Clear the old status immediately so a stale icon is never shown for a changed value.
      tap(() => setVatStatus('idle')),
      debounceTime(VAT_DEBOUNCE_MS),
      switchMap(({ vat, countryId }) => {
        if (vat.length < VAT_MIN_LENGTH || !countryId) {
          return of<VatFieldStatus>('idle');
        }
        setVatStatus('checking');
        return this.checkoutService.validateVat(vat, countryId).pipe(
          // Older backends only return isValid.
          map(response => response.status ?? (response.isValid ? 'Valid' : 'Invalid')),
          // VIES down, rate limited (429) or network error: we can't say the number is wrong.
          catchError(() => of<VatFieldStatus>('Unverifiable'))
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(setVatStatus);
  }

  // Validators.required accepts false, so an unticked box would pass once it had been ticked and unticked.
  private customFieldControl(field: any): any[] {
    switch (field.fieldType) {
      case CHECKBOX_FIELD_TYPE: return [false, Validators.requiredTrue];
      case INTEGER_FIELD_TYPE: return ['', [...requiredText, wholeNumber]];
      default: return ['', requiredText];
    }
  }

  isIntegerField(field: any): boolean {
    return field.fieldType === INTEGER_FIELD_TYPE;
  }

  isRequired(name: string): boolean {
    const control = this.checkoutForm.get(name);
    return !!control && (control.hasValidator(Validators.required) || control.hasValidator(Validators.requiredTrue));
  }

  /** Message to show under a field, once the buyer has left it or tried to submit. */
  fieldError(name: string): string | null {
    const control = this.checkoutForm.get(name);
    if (!control || control.valid || !control.touched) {
      return null;
    }
    if (name === 'terms_and_conditions') {
      return 'Please agree to the terms and conditions to continue.';
    }
    if (control.hasValidator(Validators.requiredTrue)) {
      return 'Please tick this box to continue.';
    }
    if (control.hasError('required')) {
      return 'This field is required.';
    }
    if (control.hasError('wholeNumber')) {
      return 'Enter a whole number, e.g. 12.';
    }
    if (control.hasError('maxlength')) {
      return `Use at most ${control.getError('maxlength').requiredLength} characters.`;
    }
    if (control.hasError('email') || control.hasError('pattern')) {
      return 'Enter a valid email address, e.g. name@company.com.';
    }
    if (control.hasError('countryMismatch')) {
      return VAT_STATUS_MESSAGES.CountryMismatch;
    }
    return 'This value is not valid.';
  }

  describedBy(name: string, ...extraIds: (string | null)[]): string | null {
    const ids = [this.fieldError(name) ? `${name}_error` : null, ...extraIds].filter(id => !!id);
    return ids.length ? ids.join(' ') : null;
  }

  submitForm() {
    if (this.paywallService.purchaseBlockedReason()) {
      return;
    }

    if (this.checkoutForm.invalid) {
      this.checkoutForm.markAllAsTouched();
      return;
    }

    console.log('Form submitted:', this.checkoutForm.value);

    // Sent without surrounding spaces, so the account and Stripe customer get the values the buyer meant.
    const values: any = Object.fromEntries(
      Object.entries(this.checkoutForm.value).map(([key, value]) => [key, trimmed(value)]));

    const formData: any = {
      Street: values.street,
      Street2: values.street_2,
      Zip: values.zip,
      City: values.city,
      State: values.state,
      CountryId: values.country,
      CompanyName: values.company_legal_name,
      OrganizationNumber: values.org_number,
      TaxRegistrationNumber: values.vat_number,
      CurrencyCode: values.currencyCode,
      ProductId: this.paywallService.productForSelection()?.id,
      ChargePlans: this.paywallService.purchaseChargePlans().map(item => ({
        ChargePlanId: item.chargePlanId,
        Quantity: item.quantity
      })),
      Referer: 'http://localhost:4200',
      Email: values.email,
      BillingPeriod: values.billingPeriod
    };
    this.paywallService.persistSelection();

    formData.AccountCustomFields = {};
    formData.OrderCustomFields = {};

    this.accountFields.forEach((field: any) => {
      formData.AccountCustomFields[field.fieldName] = values[field.id].toString();
    });

    this.orderFields.forEach((field: any) => {
      formData.OrderCustomFields[field.fieldName] = values[field.id].toString();
    });

    console.log('Prepared form data:', formData);

    this.checkoutService.loadingState.set({
      loading: true,
      error: false,
      message: 'Processing payment...'
    });

    // Connected before the purchase, so no account or order is created that the buyer never hears back about.
    this.checkoutService.startConnection().pipe(
      switchMap(() => this.checkoutService.purchaseSubscription(formData)),
      switchMap(response => {
        console.log('Purchase successful:', response);
        localStorage.setItem('sessionId', response.sessionId);

        if (response.sessionToken) {
          localStorage.setItem('sessionToken', response.sessionToken);
          return this.checkoutService.sendMessage("RegisterSession", response.sessionToken);
        }
        localStorage.removeItem('sessionToken');
        return this.checkoutService.sendMessage("Register", response.sessionId);
      })
    ).subscribe({
      error: (error: unknown) => {
        console.error('Purchase failed:', error);
        this.checkoutService.loadingState.set({
          loading: false,
          error: true,
          message: error instanceof HttpErrorResponse ? this.purchaseErrorMessage(error) : PAYWALL_FAILURE_MESSAGE
        });
      }
    });
  }


  goBack(): void {
    this.paywallService.step.set('cards');
  }

  /**
   * What the buyer sees when the purchase request fails. A rejected purchase is a ValidationProblem whose
   * messages are listed; any other failure (no body, plain text, network error) still gets the general message,
   * so the buyer is never left without one.
   */
  purchaseErrorMessage(error: HttpErrorResponse): string {
    return [PURCHASE_REJECTED_MESSAGE, ...validationMessages(error)].join('\n');
  }

}
